import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "./App.css";

// Eagerly load only the landing/login (initial paint).
// Everything else is code-split via React.lazy so the initial JS bundle stays tiny.
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";

const AdminDashboard        = lazy(() => import("./pages/AdminDashboard"));
const StudentDashboard      = lazy(() => import("./pages/StudentDashboard"));
const CourseDetail          = lazy(() => import("./pages/CourseDetail"));
const TestPlayer            = lazy(() => import("./pages/TestPlayer"));
const TestResults           = lazy(() => import("./pages/TestResults"));
const UserManagement        = lazy(() => import("./pages/UserManagement"));
const CourseManagement      = lazy(() => import("./pages/CourseManagement"));
const TestManagement        = lazy(() => import("./pages/TestManagement"));
const Statistics            = lazy(() => import("./pages/Statistics"));
const PracticeManagement    = lazy(() => import("./pages/PracticeManagement"));
const DiplomaManagement     = lazy(() => import("./pages/DiplomaManagement"));
const TutoringManagement    = lazy(() => import("./pages/TutoringManagement"));
const AdminMessages         = lazy(() => import("./pages/AdminMessages"));
const StudentPractices      = lazy(() => import("./pages/StudentPractices"));
const StudentDiplomas       = lazy(() => import("./pages/StudentDiplomas"));
const StudentTutoring       = lazy(() => import("./pages/StudentTutoring"));
const StudentMessages       = lazy(() => import("./pages/StudentMessages"));
const AdminResources        = lazy(() => import("./pages/AdminResources"));
const StudentResources      = lazy(() => import("./pages/StudentResources"));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A1128]">
    <div className="spinner" />
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // React to notification clicks forwarded by the service worker
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data && event.data.type === "navigate" && event.data.url) {
        try {
          const u = new URL(event.data.url, window.location.origin);
          if (u.origin === window.location.origin) navigate(u.pathname + u.search);
        } catch { /* ignore */ }
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);

  if (loading) return <LoadingSpinner />;

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={user ? <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} /> : <LoginPage />} />

        {/* Admin Routes */}
        <Route path="/admin"             element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/users"       element={<ProtectedRoute allowedRoles={["admin"]}><UserManagement /></ProtectedRoute>} />
        <Route path="/admin/courses"     element={<ProtectedRoute allowedRoles={["admin"]}><CourseManagement /></ProtectedRoute>} />
        <Route path="/admin/tests"       element={<ProtectedRoute allowedRoles={["admin"]}><TestManagement /></ProtectedRoute>} />
        <Route path="/admin/practices"   element={<ProtectedRoute allowedRoles={["admin"]}><PracticeManagement /></ProtectedRoute>} />
        <Route path="/admin/diplomas"    element={<ProtectedRoute allowedRoles={["admin"]}><DiplomaManagement /></ProtectedRoute>} />
        <Route path="/admin/tutoring"    element={<ProtectedRoute allowedRoles={["admin"]}><TutoringManagement /></ProtectedRoute>} />
        <Route path="/admin/messages"    element={<ProtectedRoute allowedRoles={["admin"]}><AdminMessages /></ProtectedRoute>} />
        <Route path="/admin/resources"   element={<ProtectedRoute allowedRoles={["admin"]}><AdminResources /></ProtectedRoute>} />
        <Route path="/admin/statistics"  element={<ProtectedRoute allowedRoles={["admin"]}><Statistics /></ProtectedRoute>} />

        {/* Student Routes */}
        <Route path="/dashboard"                        element={<ProtectedRoute allowedRoles={["student"]}><StudentDashboard /></ProtectedRoute>} />
        <Route path="/practices"                        element={<ProtectedRoute allowedRoles={["student"]}><StudentPractices /></ProtectedRoute>} />
        <Route path="/diplomas"                         element={<ProtectedRoute allowedRoles={["student"]}><StudentDiplomas /></ProtectedRoute>} />
        <Route path="/tutoring"                         element={<ProtectedRoute allowedRoles={["student"]}><StudentTutoring /></ProtectedRoute>} />
        <Route path="/messages"                          element={<ProtectedRoute allowedRoles={["student"]}><StudentMessages /></ProtectedRoute>} />
        <Route path="/resources"                         element={<ProtectedRoute allowedRoles={["student"]}><StudentResources /></ProtectedRoute>} />
        <Route path="/course/:courseId"                 element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
        <Route path="/test/:testId"                     element={<ProtectedRoute><TestPlayer /></ProtectedRoute>} />
        <Route path="/test/:testId/results/:attemptId"  element={<ProtectedRoute><TestResults /></ProtectedRoute>} />
        <Route path="/statistics"                       element={<ProtectedRoute><Statistics /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
